import React, { ReducerAction } from 'react';

import { TranslatableTextField } from '@lumapps/languages/components/TranslatableTextField/TranslatableTextField';
import { mdiFormatTitle } from '@lumapps/lumx/icons';
import { GLOBAL, TranslatableObject, useTranslate } from '@lumapps/translations';

import { State, reducer, actions } from '../../../ducks/addNavigationItemSlice';
import { isNavigationItemTitleValid } from '../../../utils';

export interface TitleFieldProps {
    /** The current form state */
    state: State;
    /** Callback to dispatch an action to the form */
    dispatch: React.Dispatch<ReducerAction<typeof reducer>>;
    /** Languages for the translatable field */
    languages: Array<string>;
    /** The default language to use for the translatable field */
    defaultLanguage: string;
    /** The field's label */
    label: string;
    /** Ref to set on the text input  */
    inputRef?: React.RefObject<HTMLInputElement>;
    /** Scope for tracking purposes  */
    scope: string;
    /** Whether the title or nodeTitle field should be edited on change  */
    fieldToEdit: 'nodeTitle' | 'title';
    /** Optional helper text for the field */
    helper?: string;
}

/**
 * Field to manage the title of a navigation item.
 * This component is meant to be used with the NavigationItemModal component and expects to receive
 * its state and dispatch function to manipulate the state.
 */
export const TitleField: React.FC<TitleFieldProps> = ({
    state,
    dispatch,
    languages,
    defaultLanguage,
    label,
    inputRef,
    scope,
    fieldToEdit,
    helper,
}) => {
    const { translateKey } = useTranslate();

    const isRequired = fieldToEdit === 'title';

    const onBlur = () => {
        dispatch(
            actions.setFieldError({
                field: fieldToEdit,
                hasError: state.touched.title && !isNavigationItemTitleValid(state.navigationMapItem),
            }),
        );
    };

    const requiredManagementProps = isRequired
        ? {
              isRequired,
              onBlur,
              hasError: state.errors.title,
              error: state.errors.title ? translateKey(GLOBAL.ERROR_MANDATORY_FIELD) : undefined,
          }
        : undefined;

    return (
        <TranslatableTextField
            languages={languages}
            defaultLanguage={defaultLanguage}
            isInModal
            values={state.navigationMapItem[fieldToEdit] || {}}
            onChange={(ns: Record<string, string> | TranslatableObject) =>
                dispatch(
                    actions.updateNavigationMapItem({
                        [fieldToEdit]: ns,
                    }),
                )
            }
            textField={{
                label,
                icon: mdiFormatTitle,
                inputRef,
                helper,
                ...requiredManagementProps,
            }}
            onDropdownClose={isRequired ? onBlur : undefined}
            dropdownClassName="lumx-spacing-padding"
            useApiv2Format
            scope={scope}
        />
    );
};
